import { API_BASE_URL, ApiError, networkError, toApiError } from './apiClient';
import { getAccessToken } from './supabase';

/**
 * Authenticated file downloads.
 *
 * The export endpoint answers with bytes and a `Content-Disposition`, not JSON,
 * so it cannot go through `apiClient` — but everything else about the call is
 * the same: the Supabase access token travels in the `Authorization` header (a
 * token never goes in a URL), and a failure is parsed into an `ApiError` so the
 * caller can read its domain code.
 */

/**
 * Filename out of a `Content-Disposition` header.
 *
 * Handles both `filename="…"` and RFC 5987 `filename*=UTF-8''…`, preferring the
 * latter when both are present. CORS exposes the header (backend `expose_headers`),
 * but a proxy that strips it must not cost the user a name, so the caller's
 * fallback stands in.
 */
export function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;

  const extended = /filename\*\s*=\s*(?:UTF-8'[^']*')?([^;]+)/i.exec(header);
  if (extended?.[1]) {
    const value = extended[1].trim().replace(/^"|"$/g, '');
    try {
      return decodeURIComponent(value) || fallback;
    } catch {
      return value || fallback;
    }
  }

  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || fallback;
}

/**
 * Fetches a file from the API and hands it to the browser's downloader.
 *
 * Resolves once the download has been started, throws {@link ApiError} when the
 * backend refused — `EXPORT_NOT_READY` (a slide mid-re-render) and
 * `REQUEST_NOT_COMPLETE` both arrive that way and are the caller's to phrase.
 */
export async function downloadApiFile(path: string, fallbackFilename: string): Promise<void> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Not signed in.', 401, null, null);
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (caught) {
    if (caught instanceof TypeError) throw networkError();
    throw caught;
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filenameFromDisposition(
      response.headers.get('Content-Disposition'),
      fallbackFilename,
    );
    // Appended so the click counts in every browser, removed straight after.
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // `click()` starts the download synchronously, so the URL has done its job
    // by the time this runs; holding it would leak the blob for the session.
    URL.revokeObjectURL(objectUrl);
  }
}
